import { useCallback, useEffect, useRef, useState } from 'react';
import { Panel } from 'react-resizable-panels';

import { getView, useEngineStatus, type EngineView } from '../engine/prism.js';
import { fixErrorRequest } from '../lib/ai-context.js';
import { askAssistant } from '../lib/ask-ai.js';
import { logEditor } from '../state/console.js';
import { usePlay } from '../state/play.js';
import { engineScenes, useSceneSet } from '../state/scene-set.js';
import { usePrefabs } from '../state/prefabs.js';
import { KeepList, LivePanel } from './play/LivePanel.js';
import { GameConsole } from './play/GameConsole.js';
import { useCommands } from '../lib/shortcuts/index.js';
import './play/play-tools.css';
import { useProjectFiles } from '../state/project-files.js';
import { useScene } from '../state/scene.js';
import { Console } from './panels/Console.js';
import { Icon } from './ui/icons.js';
import { Handle, SplitGroup } from './ui/layout.js';
import { Viewport } from './Viewport.js';

/** Scene to run: the open scene, else the project's start scene. */
function sceneToPlay(): string | null {
  // Prefab editing mode plays the scene the prefab was opened from.
  const stage = usePrefabs.getState().stage;
  if (stage) return stage.returnTo ?? useProjectFiles.getState().startScene ?? null;
  return useScene.getState().path ?? useProjectFiles.getState().startScene ?? null;
}

/** Scene the warm Play engine has loaded (edit mode), and whether it is ready. */
const warm = { scene: null as string | null, ready: false };

/** Loads `scene` (plus its additive scenes) into the warm Play engine, still in edit mode. */
async function loadWarm(view: EngineView, scene: string): Promise<void> {
  await view.openScene(scene);
  const set = useSceneSet.getState();
  const additive = engineScenes({ scenes: set.scenes, context: [] }, scene);
  if (additive.length) await view.setAdditiveScenes(additive);
  warm.scene = scene;
}

const message = (error: unknown) => (error instanceof Error ? error.message : String(error));

/** Starts a run on the warm engine: only a mode switch when the scene is already loaded.
 * A scene that cannot be loaded or played stops the run with the reason in the console,
 * instead of leaving the Play view showing the engine still in edit mode. */
async function startRun(view: EngineView): Promise<void> {
  const scene = sceneToPlay();
  if (!scene) {
    logEditor('warn', 'Nothing to play: open a scene or set startScene in prism.project.json.');
    usePlay.getState().stop();
    return;
  }
  try {
    if (warm.scene !== scene) await loadWarm(view, scene);
    view.setPlaySpeed(usePlay.getState().speed);
    await view.play();
  } catch (error) {
    warm.scene = null;
    logEditor('error', `Play could not start ${scene}: ${message(error)}`);
    usePlay.getState().stop();
    return;
  }
  if (usePlay.getState().state === 'starting') usePlay.getState().setState('playing');
}

type PlayDevice = 'desktop' | 'phone' | 'phoneLandscape' | 'tablet';
const PLAY_DEVICES: ReadonlyArray<{ id: PlayDevice; label: string }> = [
  { id: 'desktop', label: 'Desktop' },
  { id: 'phone', label: 'Phone' },
  { id: 'phoneLandscape', label: 'Phone (landscape)' },
  { id: 'tablet', label: 'Tablet' },
];
const DEVICE_KEY = 'prism.play.device';
function readDevice(): PlayDevice {
  try {
    const v = localStorage.getItem(DEVICE_KEY) as PlayDevice | null;
    return v && PLAY_DEVICES.some((d) => d.id === v) ? v : 'desktop';
  } catch {
    return 'desktop';
  }
}
function writeDevice(d: PlayDevice): PlayDevice {
  try {
    localStorage.setItem(DEVICE_KEY, d);
  } catch {
    // this session only
  }
  return d;
}

export function PlayTab() {
  const state = usePlay((s) => s.state);
  const runId = usePlay((s) => s.runId);
  const fps = usePlay((s) => s.fps);
  const drawCalls = usePlay((s) => s.drawCalls);
  const triangles = usePlay((s) => s.triangles);
  const errors = usePlay((s) => s.errors);
  const terrainChanges = usePlay((s) => s.terrainChanges);
  const engine = useEngineStatus((s) => s.status);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const p = usePlay.getState();

  // The Play engine is created once per project and stays loaded (edit mode,
  // not rendering while hidden). Scene edits reach it live (state/scene.ts),
  // so pressing Play only switches it into play mode.
  const [ready, setReady] = useState(false);
  const onReady = useCallback(async (view: EngineView) => {
    warm.ready = false;
    warm.scene = null;
    const scene = sceneToPlay();
    try {
      if (scene) await loadWarm(view, scene);
    } catch (error) {
      // Not fatal: Play loads it again (and reports why if it still fails).
      warm.scene = null;
      logEditor('warn', `The Play engine could not preload ${scene}: ${message(error)}`);
    } finally {
      warm.ready = true;
      setReady(true);
    }
  }, []);

  // Play / Restart: start a run once the warm engine is ready.
  useEffect(() => {
    if (state !== 'starting' || !ready) return;
    const view = getView('play');
    if (view) void startRun(view);
  }, [state, runId, ready]);

  // Opening another scene while stopped preloads it, so Play stays instant.
  const openScenePath = useScene((s) => s.path);
  useEffect(() => {
    if (!ready || state !== 'stopped') return;
    const scene = sceneToPlay();
    const view = getView('play');
    if (scene && view && scene !== warm.scene) void loadWarm(view, scene);
  }, [openScenePath, ready, state]);

  // Without an engine there is nothing to run.
  useEffect(() => {
    if (state === 'starting' && (engine.state === 'missing' || engine.state === 'error')) {
      logEditor('warn', 'Play mode needs the engine runtime — run pnpm engine:build.');
    }
  }, [state, engine.state]);

  const running = state !== 'stopped';
  const scene = useScene((s) => s.path);
  const speed = usePlay((s) => s.speed);
  const tint = usePlay((s) => s.tint);
  const keptChanges = usePlay((s) => s.keptChanges);
  const [live, setLive] = useState(() => readLiveOpen());
  // Preview the game on a phone or tablet: a device-shaped stage, the mouse as a finger.
  const [device, setDevice] = useState<PlayDevice>(() => readDevice());
  useEffect(() => {
    if (ready) getView('play')?.setTouchEmulation(device !== 'desktop');
  }, [device, ready]);
  const [logTab, setLogTab] = useState<LogTab>(() => readLogTab());
  const chooseLogTab = (tab: LogTab) => setLogTab(writeLogTab(tab));
  const inspectEntity = useCallback((id: string) => {
    usePlay.getState().selectLive(id);
    setLive(writeLiveOpen(true));
  }, []);

  useCommands(
    [
      { id: 'play.step', label: 'Step Frame', category: 'Play', keys: ['Ctrl+Alt+P'], run: () => usePlay.getState().step() },
      { id: 'play.live', label: 'Toggle Live Inspector', category: 'Play', run: () => setLive((v) => writeLiveOpen(!v)) },
    ],
    [],
  );

  return (
    <div className="tab-body tab-play">
      <SplitGroup id="play-v" orientation="vertical" fallback={{ stage: 74, log: 26 }}>
        <Panel id="stage" minSize={200} className="pane">
          <div className="viewport-toolbar play-toolbar">
            <button type="button" className="tb-btn" onClick={p.restart} disabled={!running} title="Restart">
              <Icon name="restart" size={13} /> Restart
            </button>
            <button type="button" className="tb-btn" onClick={p.togglePause} disabled={state !== 'playing' && state !== 'paused'} title="Pause (F6)">
              <Icon name={state === 'paused' ? 'play' : 'pause'} size={12} /> {state === 'paused' ? 'Resume' : 'Pause'}
            </button>
            <button type="button" className="tb-btn" onClick={p.step} disabled={!running || state === 'starting'} title="Step one frame (pauses; Ctrl+Alt+P)">
              <Icon name="step" size={12} /> Step
            </button>
            <button type="button" className="tb-btn" onClick={p.stop} disabled={!running} title="Stop (Shift+F5)">
              <Icon name="stop" size={12} /> Stop
            </button>
            <label className="play-speed" title="Play speed (editor time scale; scripts' set_time_scale still applies)">
              <span className="muted">Speed</span>
              <input
                type="range"
                min={0}
                max={SPEEDS.length - 1}
                step={1}
                value={nearestSpeed(speed)}
                onChange={(e) => p.setSpeed(SPEEDS[Number(e.target.value)] ?? 1)}
                aria-label="Play speed"
              />
              <button type="button" className="mono play-speed-value" title="Reset to 1×" onClick={() => p.setSpeed(1)}>
                {speed}×
              </button>
            </label>
            <label className="play-tint-pick" title="Tint the editor while playing">
              <input type="checkbox" checked={tint.enabled} onChange={(e) => p.setTint({ enabled: e.target.checked })} aria-label="Play mode tint" />
              <input type="color" value={tint.color} onChange={(e) => p.setTint({ color: e.target.value })} aria-label="Play mode tint colour" />
            </label>
            <button
              type="button"
              className={`tb-btn${live ? ' is-active' : ''}`}
              onClick={() => setLive((v) => writeLiveOpen(!v))}
              title="Live Hierarchy and Inspector of the running game"
            >
              <Icon name="panelRight" size={13} /> Live
            </button>
            <button
              type="button"
              className="tb-btn"
              onClick={() => void stageRef.current?.requestFullscreen?.()}
              title="Fullscreen"
            >
              <Icon name="fullscreen" size={13} /> Fullscreen
            </button>
            <label className="play-device" title="Preview on a phone or tablet (the mouse acts as a finger, touch controls show)">
              <Icon name="phone" size={13} />
              <select value={device} onChange={(e) => setDevice(writeDevice(e.target.value as PlayDevice))} aria-label="Preview device">
                {PLAY_DEVICES.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>
            <span className="tb-spacer" />
            <span className="muted tb-caption mono">{sceneToPlay() ?? scene ?? 'no scene'}</span>
            {errors.length > 0 && (
              <button
                type="button"
                className="tb-btn is-ai"
                onClick={() => void askAssistant(fixErrorRequest({ scenePath: sceneToPlay(), errors }))}
                title="Send the error log to Prisma"
              >
                <Icon name="sparkles" size={13} /> Ask AI to fix ({errors.length})
              </button>
            )}
          </div>
          <KeepList />
          <div className={`play-body${live && running ? ' has-live' : ''}`}>
          <div className="play-stage" ref={stageRef} data-device={device}>
            <div className="play-warm" hidden={!running}>
              <Viewport mode="play" onReady={onReady}>
                {state === 'starting' && (
                  // Until the run has started the Play engine is still in edit mode:
                  // cover it rather than show what looks like the scene editor.
                  <div className="play-loading" role="status">
                    <span className="play-loading-spinner" aria-hidden="true" />
                    <strong>{ready ? 'Starting the game…' : `Loading ${sceneToPlay() ?? 'the scene'} for Play…`}</strong>
                  </div>
                )}
                {running && engine.state === 'ready' && (
                <div className="stats-overlay mono">
                  <span>
                    <b>{Math.round(fps)}</b> fps
                  </span>
                  <span>{drawCalls} draws</span>
                  <span>{(triangles / 1000).toFixed(1)}k tris</span>
                  <span className={`play-pill is-${state}`}>{state}</span>
                </div>
                )}
              </Viewport>
            </div>
            {!running && (
              <div className="viewport play-idle">
                <button type="button" className="play-big" onClick={p.play} title="Play (F5)">
                  <Icon name="play" size={30} />
                </button>
                <strong>Press Play to run the game</strong>
                <span className="muted">
                  Runs {sceneToPlay() ?? 'the open scene'} in the Prism Engine Cloud runtime · <kbd className="kbd">F5</kbd>
                </span>
                {keptChanges && (
                  <div className="play-terrain-changes" role="status">
                    <span>
                      Kept play-mode changes: {keptChanges.labels.join(', ') || 'none'}
                      {keptChanges.skipped.length > 0 && <span className="muted"> (gone or not in the scene: {keptChanges.skipped.join(', ')})</span>}
                    </span>
                    <span className="play-terrain-actions">
                      <button type="button" className="tb-btn" onClick={p.applyKept} disabled={keptChanges.commands.length === 0} title="Write them to the scene (one undo step)">
                        Apply to scene
                      </button>
                      <button type="button" className="tb-btn" onClick={p.discardKept}>
                        Discard
                      </button>
                    </span>
                  </div>
                )}
                {terrainChanges && (
                  <div className="play-terrain-changes" role="status">
                    <span>
                      The terrain changed during play ({terrainChanges.edits} edit{terrainChanges.edits === 1 ? '' : 's'},{' '}
                      <span className="mono">{terrainChanges.path}</span>). Play-mode changes are discarded unless you keep them.
                    </span>
                    <span className="play-terrain-actions">
                      <button type="button" className="tb-btn" onClick={p.keepTerrain} title="Write the play-mode terrain to its data file">
                        Keep play-mode terrain changes
                      </button>
                      <button type="button" className="tb-btn" onClick={p.discardTerrain}>
                        Discard
                      </button>
                    </span>
                  </div>
                )}
                {!ready && engine.state === 'ready' && (
                  <span className="muted play-warming">Loading the game in the background…</span>
                )}
              </div>
            )}
          </div>
          {live && running && <LivePanel />}
          </div>
        </Panel>
        <Handle orientation="horizontal" />
        <Panel id="log" minSize={80} collapsible className="pane">
          <div className="play-log">
            <div className="play-log-tabs" role="tablist" aria-label="Play logs">
              <button type="button" role="tab" aria-selected={logTab === 'console'} className={logTab === 'console' ? 'is-active' : ''} onClick={() => chooseLogTab('console')}>
                Console
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={logTab === 'game'}
                className={logTab === 'game' ? 'is-active' : ''}
                onClick={() => chooseLogTab('game')}
                title="Everything that happens in the running game (the trace the assistant reads)"
              >
                Game console
              </button>
            </div>
            {logTab === 'game' ? (
              <GameConsole onSelectEntity={inspectEntity} />
            ) : (
              <Console
                actions={
                  errors.length > 0 ? (
                    <button
                      type="button"
                      className="tb-btn is-ai"
                      onClick={() => void askAssistant(fixErrorRequest({ scenePath: sceneToPlay(), errors }))}
                    >
                      <Icon name="sparkles" size={12} /> Ask AI to fix
                    </button>
                  ) : undefined
                }
              />
            )}
          </div>
        </Panel>
      </SplitGroup>
    </div>
  );
}

/** Play speed steps of the slider. */
const SPEEDS = [0, 0.1, 0.25, 0.5, 1, 1.5, 2, 4, 8];

function nearestSpeed(speed: number): number {
  let best = 0;
  SPEEDS.forEach((v, i) => {
    if (Math.abs(v - speed) < Math.abs(SPEEDS[best]! - speed)) best = i;
  });
  return best;
}

type LogTab = 'console' | 'game';
const LOG_TAB_KEY = 'prism.playLogTab';

function readLogTab(): LogTab {
  try {
    return localStorage.getItem(LOG_TAB_KEY) === 'game' ? 'game' : 'console';
  } catch {
    return 'console';
  }
}

function writeLogTab(tab: LogTab): LogTab {
  try {
    localStorage.setItem(LOG_TAB_KEY, tab);
  } catch {
    // Storage blocked.
  }
  return tab;
}

const LIVE_KEY = 'prism.playLive';

function readLiveOpen(): boolean {
  try {
    return localStorage.getItem(LIVE_KEY) !== 'false';
  } catch {
    return true;
  }
}

function writeLiveOpen(open: boolean): boolean {
  try {
    localStorage.setItem(LIVE_KEY, String(open));
  } catch {
    // Storage blocked.
  }
  return open;
}
